import { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  getRedirectResult
} from 'firebase/auth';
import { User } from '@firebase/auth-types';
import { auth } from '../firebase';
import axios from 'axios';
import { userService, api } from '../services/api';
import { logStorageState } from '../firebase';

// Type pour les données utilisateur provenant du backend
interface UserData {
  userId?: number;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  firebaseUid?: string;
  createdAt?: string;
  updatedAt?: string;
  isNewUser?: boolean;
}

// Type pour le contexte d'authentification
interface AuthContextType {
  currentUser: User | null;
  userData: UserData | null;
  user: UserData | null; // Alias pour userData pour compatibilité
  isLoggedIn: boolean;
  isNewUser: boolean;
  isRecentLogin: boolean;
  setIsRecentLogin: (value: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, firstName?: string, lastName?: string, userType?: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  processUser: (user: User) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  return useContext(AuthContext) as AuthContextType;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isRecentLogin, setIsRecentLogin] = useState(false);

  // Helper pour masquer les emails dans les logs
  const maskEmail = (email: string | null): string => {
    if (!email) return 'email-inconnu';
    const [username, domain] = email.split('@');
    if (!username || !domain) return 'email-invalide';
    return `${username.substring(0, 2)}***@${domain}`;
  };

  // Helper pour masquer les UIDs dans les logs
  const maskUid = (uid: string): string => {
    if (!uid) return 'uid-inconnu';
    return `${uid.substring(0, 4)}***${uid.substring(uid.length - 4)}`;
  };

  // Fonction pour charger les données utilisateur
  const loadUserData = async (firebaseUser: User) => {
    try {
      console.log(`🔄 Chargement données utilisateur: ${maskEmail(firebaseUser.email)}`);
      
      // Obtenir un token frais et le stocker
      const token = await firebaseUser.getIdToken(true);
      const tokenKey = `firebaseAuthToken_${firebaseUser.uid}`;
      sessionStorage.setItem(tokenKey, token);
      sessionStorage.setItem('currentUserUID', firebaseUser.uid);
      
      // Configurer l'en-tête d'autorisation pour les requêtes API
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      try {
        // Récupérer les données utilisateur du backend
        const userData = await userService.getCurrentUser();
        
        // Vérification de sécurité - emails doivent correspondre
        if (userData.email !== firebaseUser.email) {
          console.error(`⚠️ ERREUR DE SÉCURITÉ: Discordance d'identité!`);
          sessionStorage.clear();
          localStorage.clear();
          await signOut(auth);
          throw new Error("Erreur de sécurité: discordance d'identité. Veuillez vous reconnecter.");
        }
        
        setUserData(userData);
        setIsNewUser(userData.isNewUser === true);
        return userData;
      } catch (apiError: any) {
        if (apiError.response && apiError.response.status === 401) {
          // Utilisateur Firebase existe mais pas dans notre base
          console.log("ℹ️ Nouvel utilisateur détecté (pas en base de données)");
          setIsNewUser(true);
          
          const minimalUserData: UserData = {
            email: firebaseUser.email || '',
            isNewUser: true
          };
          
          setUserData(minimalUserData);
          return minimalUserData;
        }
        
        throw apiError;
      }
    } catch (error: any) {
      console.error("❌ Erreur chargement données utilisateur:", error.message);
      throw error;
    }
  };
  
  // Observer les changements d'état d'authentification Firebase
  useEffect(() => {
    console.log("🔢 ÉTAPE 10: Initialisation de l'observateur d'authentification dans AuthContext");
    
    // Fonction de gestion de l'observateur
    let unsubscribe: () => void;
    let hasCheckedRedirect = false;
    
    // Configurer d'abord l'observateur d'authentification
    console.log("🔢 ÉTAPE 11: Configuration de l'observateur onAuthStateChanged");
    unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      console.log(`🔢 ÉTAPE 12: Changement d'état d'authentification détecté: ${user ? 'Connecté' : 'Déconnecté'}`);
      
      // NOUVEAU FLUX: Maintenant on traite le résultat de redirection à l'intérieur du callback
      // mais seulement lors du premier changement d'état
      if (!hasCheckedRedirect) {
        console.log("🔢 ÉTAPE 13: Premier changement d'état, vérification du résultat de redirection");
        hasCheckedRedirect = true;
        
        try {
          console.log("🔢 ÉTAPE 14: État de stockage avant getRedirectResult:");
          logStorageState();
          
          // Retarder légèrement l'appel à getRedirectResult pour donner à Firebase le temps de se stabiliser
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Vérifier d'abord si un utilisateur est déjà disponible
          if (auth.currentUser) {
            console.log(`🔢 ÉTAPE 14.1: Utilisateur déjà disponible: ${auth.currentUser.email}`);
            await processUser(auth.currentUser);
            // Nettoyer le flag de redirection
            sessionStorage.removeItem('sso_redirect_triggered');
            return;
          }
          
          console.log("🔢 ÉTAPE 14.2: Tentative de récupération du résultat de redirection...");
          
          // Tentative 1: getRedirectResult standard
          try {
            const result = await getRedirectResult(auth);
            
            // Analyser le résultat
            const redirectResult = {
              hasResult: result !== null,
              hasUser: result?.user !== null && result?.user !== undefined,
              operationType: result?.operationType || 'aucun'
            };
            console.log("🔢 ÉTAPE 15: Résultat de getRedirectResult obtenu:", redirectResult);
            
            if (result && result.user) {
              // Utilisateur récupéré avec succès
              console.log(`🔢 ÉTAPE 16: Utilisateur récupéré après redirection: ${result.user.email}`);
              await processUser(result.user);
              setCurrentUser(result.user);
              sessionStorage.removeItem('sso_redirect_triggered');
              return;
            }
          } catch (redirectError) {
            console.error("🔢 ERREUR à l'ÉTAPE 15 (getRedirectResult):", redirectError);
          }
          
          // Tentative 2: Rechercher dans le localStorage pour les données Firebase
          console.log("🔢 ÉTAPE 16.0: Tentative de récupération alternative des données Firebase");
          const isRedirectContext = sessionStorage.getItem('sso_redirect_triggered') === 'true';
          if (isRedirectContext) {
            console.log("🔢 ÉTAPE 16.1: Contexte de redirection détecté, recherche de données alternatives");
            
            // Attendre un peu plus longtemps pour que Firebase ait le temps de finaliser l'authentification
            console.log("🔢 ÉTAPE 16.2: Attente supplémentaire pour stabilisation Firebase");
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Vérifier à nouveau si l'utilisateur est disponible
            if (auth.currentUser) {
              console.log(`🔢 ÉTAPE 16.3: Utilisateur récupéré après délai: ${auth.currentUser.email}`);
              await processUser(auth.currentUser);
              setCurrentUser(auth.currentUser);
              sessionStorage.removeItem('sso_redirect_triggered');
              return;
            }
            
            // Tentative 3: Utiliser onAuthStateChanged comme dernier recours
            console.log("🔢 ÉTAPE 16.4: Configuration d'un observateur temporaire supplémentaire");
            const tempUnsubscribe = onAuthStateChanged(auth, async (tempUser: User | null) => {
              if (tempUser) {
                console.log(`🔢 ÉTAPE 16.5: Utilisateur récupéré par observateur temporaire: ${tempUser.email}`);
                tempUnsubscribe(); // Désabonner immédiatement
                await processUser(tempUser);
                setCurrentUser(tempUser);
                sessionStorage.removeItem('sso_redirect_triggered');
              }
            });
            
            // Ajouter un timeout pour l'observer temporaire
            setTimeout(() => {
              tempUnsubscribe();
              console.log("🔢 ÉTAPE 16.6: Timeout de l'observateur temporaire atteint");
            }, 5000);
          } else {
            console.log("🔢 ÉTAPE 16.1: Pas d'utilisateur dans le résultat de redirection");
          }
        } catch (redirectError) {
          console.error("🔢 ERREUR lors de la récupération du résultat de redirection:", redirectError);
        }
      }
      
      // Continuer avec le traitement normal de l'utilisateur
      console.log("🔢 ÉTAPE 20: Traitement de l'utilisateur après vérification de redirection");
      
      if (user) {
        console.log(`🔢 ÉTAPE 21: Utilisateur authentifié: ${maskUid(user.uid)}`);
        setCurrentUser(user);
        
        try {
          await processUser(user);
        } catch (processError) {
          console.error("🔢 ERREUR lors du traitement de l'utilisateur:", processError);
        }
      } else {
        console.log("🔢 ÉTAPE 21.1: Aucun utilisateur authentifié");
        setCurrentUser(null);
        setUserData(null);
        setIsNewUser(false);
      }
      
      console.log("🔢 ÉTAPE 25: Fin du traitement d'authentification, loading=false");
      setLoading(false);
    });
    
    // Fonction de nettoyage
    return () => {
      console.log("🧹 Nettoyage de l'observateur d'authentification dans AuthContext - État avant nettoyage:");
      console.log("🍪 Cookies:", document.cookie);
      console.log("📦 LocalStorage:", { ...localStorage });
      console.log("🗑️ SessionStorage:", { ...sessionStorage });
      logStorageState();
      
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);
  
  // Fonction de connexion
  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      console.log(`✅ Connexion réussie: ${maskEmail(email)}`);
      setIsRecentLogin(true);
    } catch (error: any) {
      console.error("❌ Erreur connexion:", error.message);
      throw new Error(error.message || "Erreur lors de la connexion");
    }
  };

  // Fonction d'inscription
  const register = async (email: string, password: string, name: string, firstName?: string, lastName?: string, userType?: string) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      console.log("✅ Compte Firebase créé:", maskEmail(email));
      setIsRecentLogin(true);
      
      // Configurer le token
      const token = await user.getIdToken(true);
      const tokenKey = `firebaseAuthToken_${user.uid}`;
      sessionStorage.setItem(tokenKey, token);
      sessionStorage.setItem('currentUserUID', user.uid);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      try {
        // Enregistrer l'utilisateur dans le backend
        userService.cleanupAuthData(false);
        const response = await api.post('/api/users/register', { 
          email, 
          name,
          firstName,
          lastName,
          userType,
          firebaseUid: user.uid
        });
        
        if (response.data && response.data.user) {
          setUserData(response.data.user);
          setIsNewUser(false);
          
          // Stocker le rôle de l'utilisateur si disponible
          const userData = await loadUserData(user);
          if (userData?.role) {
            sessionStorage.setItem('userRole', userData.role);
          }
        }
      } catch (backendError) {
        console.error("❌ Erreur backend inscription:", backendError);
        setIsNewUser(true);
      }
    } catch (firebaseError: any) {
      console.error("❌ Erreur création compte Firebase:", firebaseError.message);
      throw new Error(firebaseError.message || "Erreur lors de l'inscription");
    }
  };

  // Fonction de déconnexion
  const logout = async () => {
    try {
      const currentUid = sessionStorage.getItem('currentUserUID');
      const userEmail = auth.currentUser?.email;
      
      console.log(`✅ Déconnexion: ${maskEmail(userEmail || "Utilisateur inconnu")}`);
      
      // Nettoyer les tokens
      if (currentUid) {
        sessionStorage.removeItem(`firebaseAuthToken_${currentUid}`);
      }
      
      sessionStorage.removeItem('firebaseAuthToken');
      sessionStorage.removeItem('currentUserUID');
      delete axios.defaults.headers.common['Authorization'];
      
      // Déconnexion de Firebase
      await signOut(auth);
      userService.cleanupAuthData();
      
      // Supprimer tous les tokens résiduels
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('firebaseAuthToken_')) {
          sessionStorage.removeItem(key);
        }
      });
      
      setUserData(null);
      setIsNewUser(false);
      setIsRecentLogin(false);
    } catch (error) {
      console.error("❌ Erreur déconnexion:", error);
    }
  };

  /**
   * Traite l'utilisateur authentifié, récupère ses données et configure le contexte
   */
  const processUser = async (firebaseUser: User) => {
    try {
      console.log("🔍 processUser: Début du traitement de l'utilisateur Firebase");
      
      // Vérifier si nous sommes dans un contexte post-redirection
      const isRedirectContext = sessionStorage.getItem('sso_redirect_triggered') === 'true';
      if (isRedirectContext) {
        console.log("🔄 processUser: Contexte post-redirection SSO détecté");
      }
      
      // Si aucun utilisateur Firebase, déconnexion
      if (!firebaseUser) {
        console.log("⚠️ processUser: Aucun utilisateur Firebase fourni");
        
        // Vérifier dans sessionStorage si nous avons un UID récupéré
        const recoveredUid = sessionStorage.getItem('recovered_firebase_uid');
        if (isRedirectContext && recoveredUid) {
          console.log(`🔄 processUser: UID récupéré trouvé: ${recoveredUid}, tentative de récupération manuelle`);
          // Attendre un peu pour laisser Firebase finaliser la récupération d'état
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Vérifier à nouveau l'utilisateur Firebase
          const currentUser = auth.currentUser;
          if (currentUser) {
            console.log(`🔄 processUser: Utilisateur Firebase récupéré manuellement: ${currentUser.email}`);
            // Continuer le traitement avec l'utilisateur récupéré
            return processUser(currentUser);
          }
        }
        
        // Si toujours pas d'utilisateur, nettoyer et sortir
        setCurrentUser(null);
        setUserData(null);
        setIsNewUser(false);
        return;
      }
      
      // Récupérer le token Firebase pour les requêtes API
      const token = await firebaseUser.getIdToken();
      // Stocker le token pour les requêtes API
      const tokenKey = `firebaseAuthToken_${firebaseUser.uid}`;
      sessionStorage.setItem(tokenKey, token);
      
      // Si nous sommes en contexte post-redirection, attendre un peu plus longtemps
      // pour s'assurer que les systèmes backend ont eu le temps de se synchroniser
      if (isRedirectContext) {
        console.log("🕒 processUser: Délai supplémentaire en contexte post-redirection");
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      // Tenter de récupérer les données utilisateur du backend
      try {
        console.log(`🔍 processUser: Récupération des données utilisateur pour ${firebaseUser.email}`);
        const existingUser = await userService.getCurrentUser();
        
        console.log("📄 processUser: Données utilisateur récupérées:", existingUser);
        console.log("📝 Champs critiques:", {
          userId: existingUser?.userId,
          role: existingUser?.role,
          email: existingUser?.email,
          firebaseUid: existingUser?.firebaseUid
        });
        
        // Vérifier si l'utilisateur existe dans la base de données et a les champs obligatoires
        if (!existingUser || !existingUser.userId) {
          console.log("⚠️ processUser: Utilisateur incomplet ou inexistant dans la base de données");
          
          if (isRedirectContext) {
            console.log("🔄 processUser: Création automatique d'utilisateur après redirection SSO");
            
            // Extraire les données du sessionStorage (ajoutées lors de la redirection)
            let userData = {};
            try {
              const storedUserData = sessionStorage.getItem('userData');
              if (storedUserData) {
                userData = JSON.parse(storedUserData);
                console.log("📦 processUser: Données utilisateur récupérées de sessionStorage:", userData);
              }
            } catch (error) {
              console.error("❌ processUser: Erreur lors de la lecture des données utilisateur:", error);
            }
            
            // Créer un nouvel utilisateur avec l'UID Firebase
            const newUserData = {
              email: firebaseUser.email || '',
              firebaseUid: firebaseUser.uid,
              firstName: (userData as any).firstName || firebaseUser.displayName?.split(' ')[0] || '',
              lastName: (userData as any).lastName || firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
              password: Math.random().toString(36).slice(-12),
              confirmPassword: Math.random().toString(36).slice(-12),
            };
            
            console.log("📝 processUser: Création d'un nouvel utilisateur avec les données:", newUserData);
            
            try {
              const createdUser = await userService.register(newUserData);
              console.log("✅ processUser: Nouvel utilisateur créé avec succès:", createdUser);
              
              // Stocker les données utilisateur
              userService.storeUserData(createdUser);
              
              // Récupérer les données complètes de l'utilisateur
              const refreshedUser = await userService.getCurrentUser();
              console.log("📄 processUser: Données utilisateur rafraîchies:", refreshedUser);
              
              // Mettre à jour l'état
              setUserData(refreshedUser);
              setIsNewUser(true);
              setCurrentUser(firebaseUser);
              
              // Mettre à jour la localisation
              updateUserLocation();
              
              // Stocker dans localStorage pour la persistance
              localStorage.setItem('isLoggedIn', 'true');
              
              return;
            } catch (error) {
              console.error("❌ processUser: Erreur lors de la création de l'utilisateur:", error);
            }
          }
        } else {
          // Utilisateur existant avec toutes les données
          console.log("✅ processUser: Utilisateur existant avec toutes les données");
          
          // Mettre à jour l'état
          setUserData(existingUser);
          setIsNewUser(false);
          setCurrentUser(firebaseUser);
          
          // Mettre à jour la localisation
          updateUserLocation();
          
          // Stocker dans localStorage pour la persistance
          localStorage.setItem('isLoggedIn', 'true');
        }
      } catch (error: any) {
        console.error("❌ processUser: Erreur lors de la récupération des données utilisateur:", error);
        
        // Si l'erreur est 401, cela signifie que l'utilisateur n'existe pas dans la base de données
        if (error.response && error.response.status === 401) {
          console.log("⚠️ processUser: Statut 401 - Utilisateur non trouvé dans la base de données");
          
          // Créer un nouvel utilisateur avec l'UID Firebase
          const newUserData = {
            email: firebaseUser.email || '',
            firebaseUid: firebaseUser.uid,
            firstName: firebaseUser.displayName?.split(' ')[0] || '',
            lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
            password: Math.random().toString(36).slice(-12),
            confirmPassword: Math.random().toString(36).slice(-12),
          };
          
          console.log("📝 processUser: Création d'un nouvel utilisateur avec les données:", newUserData);
          
          try {
            const createdUser = await userService.register(newUserData);
            console.log("✅ processUser: Nouvel utilisateur créé avec succès:", createdUser);
            
            // Stocker les données utilisateur
            userService.storeUserData(createdUser);
            
            // Récupérer les données complètes de l'utilisateur
            const refreshedUser = await userService.getCurrentUser();
            console.log("📄 processUser: Données utilisateur rafraîchies:", refreshedUser);
            
            // Mettre à jour l'état
            setUserData(refreshedUser);
            setIsNewUser(true);
            setCurrentUser(firebaseUser);
            
            // Mettre à jour la localisation
            updateUserLocation();
            
            // Stocker dans localStorage pour la persistance
            localStorage.setItem('isLoggedIn', 'true');
          } catch (error) {
            console.error("❌ processUser: Erreur lors de la création de l'utilisateur:", error);
          }
        } else {
          console.error("❌ processUser: Erreur non gérée:", error);
        }
      }
    } catch (error) {
      console.error("❌ processUser: Erreur globale:", error);
    }
  };
  
  // Fonction pour mettre à jour la position de l'utilisateur
  const updateUserLocation = async () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        await userService.updateUserLocation({ lat: coords.latitude, lon: coords.longitude });
        console.log('✅ Position mise à jour');
      } catch (err) {
        console.error('Erreur MAJ position:', err);
      }
    });
  };

  // Valeurs à fournir dans le contexte
  const value = {
    currentUser,
    userData,
    user: userData, // Alias pour userData pour compatibilité
    isLoggedIn: !!currentUser,
    isNewUser,
    isRecentLogin,
    setIsRecentLogin,
    login,
    register,
    logout,
    loading,
    processUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
